import React, { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Select, Table, Space, message, Button } from 'antd';
import { BookOutlined } from '@ant-design/icons';
import axios from 'axios';
import styles from './index.module.css';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const CategoryManage = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [subCategories, setSubCategories] = useState({});
  const [examList, setExamList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [examType, setExamType] = useState('all');
  const [examLoading, setExamLoading] = useState(false);
  const [examSubject, setExamSubject] = useState({})
  const [timeRunning, setTimeRunning] = useState({})
  const [timer, setTimer] = useState(null)
  const navigate = useNavigate();
  const location = useLocation();

  // 获取主分类数据
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/api/categories');
        if (response.data.success) {
          console.log(response.data)
          const { mainCategories, subCategories: subCats } = response.data;
          let list = mainCategories.map(category => ({
            code: category.code,
            name: category.name,
            description: category.description,
            order: category.order,
            dbCategory: category.dbCategory
          }))
          const lastSelected = localStorage.getItem('lastSelectedCategory');
          let defaultCategory;
          if (lastSelected) {
            defaultCategory = JSON.parse(lastSelected)
            list = list.filter((item) => item.code === defaultCategory.mainCategory)
            setSelectedSubCategory(defaultCategory.subCategory)
            getExamData(defaultCategory.subCategoryName)
          }
          setCategories(list);
          setSubCategories(subCats || {});
          if(defaultCategory) {
            handleCategoryChange(defaultCategory.mainCategory)
          }
        }
      } catch (error) {
        console.error('获取分类失败:', error);
        message.error('获取分类数据失败');
      }
    };
    fetchCategories();
  }, [location]);

  // 添加处理导航状态的 effect
  useEffect(() => {
    const handleNavigationState = async () => {
      const locationState = location.state;
      if (locationState?.selectedCategory) {
        // 设置选中的主分类
        await handleCategoryChange(locationState.selectedCategory);

        // 如果有子分类，设置选中的子分类
        if (locationState.selectedSubCategory) {
          await handleSubCategoryChange(locationState.selectedSubCategory);
        }

        // 如果有目标试题集，自动点击对应的试题集
        if (locationState.targetExam) {
          const targetExam = examList.find(
            exam => exam.exam_data === locationState.targetExam.exam_data
          );
          if (targetExam) {
            handleExamClick(targetExam);
          }
        }
      }
    };

    handleNavigationState();
    window.addEventListener('beforeunload', () => {
      if(timer) clearInterval(timer)
    })
  }, [location.state]);

  const getExamData = async (major) => {
    const res = await axios.get('/api/exam/subject', {
      params: {
        major: major
      }
    })
    console.log('getExamData', res);
    if(res.data.success) {
      const data = res.data.data;
      data.examTime= dayjs(data.examTime).format('YYYY-MM-DD');
      setExamSubject(data)
      startCountdown(data.examTime)
    }
  }

  const countdown = (milliseconds) => {
    //根据拿到的时间差计算出倒计时
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    setTimeRunning({
      days: days.toString().padStart(2, '0'),
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      milliseconds: milliseconds,
    })
  }

  const startCountdown = (currentTime) => {
    // 模拟倒计时，实际应用中应该计算真实的倒计时
    let milliseconds = dayjs(currentTime).diff(dayjs(), 'milliseconds')
    countdown(milliseconds)
    const timer = setInterval(() => {
      const milliseconds = dayjs(currentTime).diff(dayjs(), 'milliseconds')
      if(milliseconds <= 0) clearInterval(timer)
      countdown(milliseconds)
    }, 1000);
    setTimer(timer)
  }

  // 表格列定义
  const columns = [
    {
      title: '试题集名称',
      dataIndex: 'exam_data',
      key: 'exam_data',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleExamClick(record)}
          className={styles.examLink}
          loading={examLoading}
        >
          {text}
        </Button>
      )
    },
    {
      title: '题目数量',
      dataIndex: 'questionCount',
      key: 'questionCount',
      width: 120,
      align: 'center',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleExamClick(record)}
          style={{ color: '#ffffffA6' }}
          loading={examLoading}
        >
          点击进入
        </Button>
      ),
    }
  ];

  // 处理主分类变化
  const handleCategoryChange = async (value) => {
    setSelectedCategory(value);
    setLoading(true);

    try {
      const response = await axios.get('/api/categories');
      if (response.data.success) {
        const subCats = response.data.subCategories || {};
        setSubCategories(subCats);

        // 获取当前选择分类的子分类列表
        const currentSubCategories = subCats[value] || [];

        // 如果有子分类，自动选择第一个
        if (currentSubCategories.length > 0) {
          let firstSubCategory = currentSubCategories[0];
          console.log('First subCategory:', firstSubCategory);
          const lastSelected = localStorage.getItem('lastSelectedCategory');
          let defaultCategory;
          if (lastSelected) {
            defaultCategory = JSON.parse(lastSelected)
            setSelectedSubCategory(defaultCategory.subCategory)
            console.log('currentSubCategories', currentSubCategories);
            firstSubCategory = currentSubCategories.find((item) => item.code === defaultCategory.subCategory)
          } else {
            setSelectedSubCategory(firstSubCategory.code);
          }

          // 加载第一个子分类的数据
          const collection = firstSubCategory.collection || 'economist_exam';
          const examResponse = await axios.get(`/api/exam/${collection}/list`, {
            params: {
              category: value,
              subCategory: firstSubCategory.code
            },
            headers: {
              'X-Module': 'category-manage'
            }
          });

          if (examResponse.data.success) {
            const examData = examResponse.data.exams || [];
            const formattedExams = examData.map(exam => ({
              ...exam,
              key: exam._id || exam.exam_data,
              exam_data: exam.exam_data || '未命名试题集',
              questionCount: exam.questionCount || 0,
              category: exam.category || firstSubCategory.name,
              updatedAt: exam.updatedAt || new Date().toISOString()
            }));
            setExamList(formattedExams);
          } else {
            setExamList([]);
            message.warning('未找到相关试题');
          }
        } else {
          setSelectedSubCategory(null);
          setExamList([]);
          message.warning('该分类下暂无子分类');
        }
      }
    } catch (error) {
      console.error('获取分类失败:', error);
      message.error('获取分类失败');
      setSelectedSubCategory(null);
      setExamList([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理二级分类变化
  const handleSubCategoryChange = async (value) => {
    setSelectedSubCategory(value);
    setLoading(true);
    try {
      const currentSubCategories = subCategories[selectedCategory] || [];
      const subCategory = currentSubCategories.find(sub => sub.code === value);

      if (subCategory) {
        const collection = subCategory.collection || 'economist_exam';
        console.log('Selected subCategory:', subCategory);

        // 获取试题列表
        const response = await axios.get(`/api/exam/${collection}/list`, {
          params: {
            category: selectedCategory,
            subCategory: value
          },
          headers: {
            'X-Module': 'category-manage'
          }
        });

        if (response.data.success) {
          const examData = response.data.exams || [];
          // 直接使用 API 返回的数据，不做修改
          const formattedExams = examData.map(exam => ({
            ...exam,
            key: exam._id || exam.exam_data,
            exam_data: exam.exam_data || '未命名试题集',
            questionCount: exam.questionCount || 0,
            category: exam.category, // 保持 API 返回的原始分类值
            updatedAt: exam.updatedAt || new Date().toISOString()
          }));
          console.log('Formatted exams:', formattedExams);
          setExamList(formattedExams);
        } else {
          setExamList([]);
          message.warning('未找到相关试题');
        }
      }
    } catch (error) {
      console.error('获取试题列表失败:', error);
      message.error('获取试题列表失败');
      setExamList([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理试题类型变化
  const handleExamTypeChange = (value) => {
    setExamType(value);
  };

  // 过滤试题列表
  const filteredExamList = useMemo(() => {
    if (examType === 'all') return examList;

    return examList.filter(exam => {
      const isSimulation = exam.exam_data.includes('模拟');
      return examType === 'simulation' ? isSimulation : !isSimulation;
    });
  }, [examList, examType]);

  // 修改卡片点击处理函数
  const handleCardClick = (category) => {
    handleCategoryChange(category.code);
  };

  // 处理试题集点击
  const handleExamClick = async (exam) => {
    try {
      setExamLoading(true);

      const currentSubCategory = subCategories[selectedCategory]?.find(
        sub => sub.code === selectedSubCategory
      );

      if (!currentSubCategory) {
        throw new Error('分类信息不完整');
      }

      // 确保使用正确的 collection
      const collection = currentSubCategory.collection || 'teacher_exam';

      // 构造导航状态
      const navigationState = {
        examInfo: {
          exam_data: exam.exam_data,
          category: exam.category,
          collection: collection // 添加 collection 到 examInfo
        },
        collection: collection
      };

      console.log('Navigating with state:', navigationState);

      // 导航到练习页面
      navigate(`/exam/practice/${collection}/${encodeURIComponent(exam.exam_data)}?examCategory=${selectedSubCategory}`, {
        state: navigationState
      });

    } catch (error) {
      message.error(error.message || '跳转失败');
      console.error('Navigation error:', error);
    } finally {
      setExamLoading(false);
    }
  };

  const TimeCard = () => {
    return (
        <div>
          <div className={styles.timeCardTitle}>
            <div className={styles.tTitle}>{examSubject.major}</div>
            <div className={styles.tValue}>{examSubject.description}</div>
          </div>
          <div className={styles.timeCard}>
            <div className={styles.timeCardItem}>
              <div className={styles.tTitle}>考试日期</div>
              <div className={styles.tValue}>{examSubject.examTime}</div>
            </div>
            <div className={styles.timeCardItem}>
              <div className={styles.tTitle}>考试时长</div>
              <div className={styles.tValue}>{examSubject.duration}分钟</div>
            </div>
            <div className={styles.timeCardItem}>
              <div className={styles.tTitle}>总分</div>
              <div className={styles.tValue}>{examSubject.totalScore}分</div>
            </div>
            <div className={styles.timeCardItem}>
              <div className={styles.tTitle}>及格分</div>
              <div className={styles.tValue}>{examSubject.passScore}分</div>
            </div>
            <div className={`${styles.timeCardItem}`}>
              <div className={styles.tTitle}>距离考试开始还有</div>
              <div className={styles.timeValue}>
                <div className={styles.timeValueItem}>
                  <div className={styles.value}>{timeRunning.days}</div>
                  <div className={styles.unit}>天</div>
                </div>
                <div className={styles.timeValueItem}>
                  <div className={styles.value}>{timeRunning.hours}</div>
                  <div className={styles.unit}>时</div>
                </div>
                <div className={styles.timeValueItem}>
                  <div className={styles.value}>{timeRunning.minutes}</div>
                  <div className={styles.unit}>分</div>
                </div>
                <div className={styles.timeValueItem}>
                  <div className={styles.value}>{timeRunning.seconds}</div>
                  <div className={styles.unit}>秒</div>
                </div>
              </div>
            </div>
          </div>
        </div>
    )
  }
  const TypeCard = () => {
    return (<div className={styles.categoryCards}>
      {categories.map(category => (
          <Card
              key={category.code}
              className={`${styles.categoryCard} ${selectedCategory === category.code ? styles.selectedCard : ''}`}
              onClick={() => handleCardClick(category)}
              hoverable
          >
            <BookOutlined className={styles.categoryIcon} />
            <Title level={4}>{category.name}</Title>
            <div className={styles.description}>{category.description}</div>
          </Card>
      ))}
    </div>)
  }

  return (
    <div className={styles.container}>
      {/* 顶部分类展示 */}
      {
        categories.length === 1 && Object.keys(examSubject).length ? <TimeCard/> : <TypeCard/>
      }

      {/* 分类筛选区域 */}
      <Card className={styles.filterCard}>
        <Space size="large">
          <Select
            style={{ width: 200 }}
            placeholder="选择主分类"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            {categories.map(category => (
              <Option key={category.code} value={category.code}>
                {category.name}
              </Option>
            ))}
          </Select>

          <Select
            style={{ width: 200 }}
            placeholder="选择二级分类"
            value={selectedSubCategory}
            onChange={handleSubCategoryChange}
            disabled={!selectedCategory}
          >
            {(subCategories[selectedCategory] || []).map(sub => (
              <Option key={sub.code} value={sub.code}>
                {sub.name}
              </Option>
            ))}
          </Select>

          <Select
            style={{ width: 200 }}
            placeholder="选择试题类型"
            value={examType}
            onChange={handleExamTypeChange}
          >
            <Option value="all">全部试题</Option>
            <Option value="real">历年真题</Option>
            <Option value="simulation">模拟试题</Option>
          </Select>
        </Space>
      </Card>

      {/* 试题列表 - 使用过滤后的列表 */}
      <Card className={styles.tableCard}>
        <Table
          columns={columns}
          dataSource={filteredExamList}
          loading={loading}
          rowKey="key"
          pagination={{
            pageSize: 10,
            showTotal: total => `共 ${total} 条数据`
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '20px 0' }}>
                <p>暂无试题数据</p>
                <small style={{ color: '#999' }}>
                  {selectedCategory ? '该分类下暂无试题' : '请选择分类查看相关试题'}
                </small>
              </div>
            )
          }}
        />
      </Card>
    </div>
  );
};

export default CategoryManage;